import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { theme } from '../config/theme';
import { movements } from '../config/movements';
import { commonVariants } from '../config/animations';
import Media from './Media/Media';
import { Button } from '../junto-frontend/components/ui';

const SectionWrapper = styled(motion.section)`
  padding: 120px 0;
  max-width: 1400px;
  margin: 0 auto;
  padding-left: 80px;
  padding-right: 80px;
  background-color: ${theme.colors.primary.background};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 80px 40px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 60px 20px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 120px;
  align-items: center;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

const ImagesColumn = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 20px;
  height: 600px;
  
  /* First image takes full left column */
  & > div:first-child {
    grid-column: 1;
    grid-row: 1 / -1;
  }
  
  /* Second and third images stack on the right */
  & > div:nth-child(2) {
    grid-column: 2;
    grid-row: 1;
  }
  
  & > div:nth-child(3) {
    grid-column: 2;
    grid-row: 2;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    order: 1;
    display: flex;
    flex-direction: row;
    gap: 15px;
    height: 250px;
    
    /* Reset grid behavior on mobile */
    & > div:first-child,
    & > div:nth-child(2),
    & > div:nth-child(3) {
      grid-column: unset;
      grid-row: unset;
      flex: 1;
    }
  }
`;

const ImageContainer = styled(motion.div)`
  overflow: hidden;
  border: 1px solid ${theme.shadows.light};
  cursor: pointer;
`;

const MediaWrapper = styled.div`
  width: 100%;
  height: 400px;
  display: block;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    height: 250px;
    width: 100%;
  }
`;

const ContentColumn = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    order: 2;
    text-align: center;
  }
`;

const Heading = styled(motion.h2)`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: ${theme.typography.primary.largeHeading.fontSize};
  font-weight: ${theme.typography.primary.largeHeading.fontWeight};
  line-height: ${theme.typography.primary.largeHeading.lineHeight};
  color: ${theme.colors.primary.text};
  margin-bottom: ${theme.spacing.lg};
  text-align: left;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    text-align: center;
  }
`;

const Selector = styled(motion.div)`
  margin-bottom: 40px;
`;

const MovementOptions = styled.div`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 1rem;
  font-weight: 400;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  line-height: 2.5;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.9rem;
    line-height: 2.2;
    gap: 4px;
  }
`;

const MovementOption = styled.span`
  color: ${props => props.$isActive ? theme.colors.primary.text : theme.colors.primary.text};
  font-weight: ${props => props.$isActive ? '600' : '300'};
  cursor: pointer;
  position: relative;
  transition: ${theme.transitions.default};
  opacity: ${props => props.$isActive ? '1' : '0.5'};
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0px;
    left: 50%;
    transform: translateX(-50%);
    width: ${props => props.$isActive ? '100%' : '0'};
    height: 1px;
    background-color: ${theme.colors.primary.text};
    transition: width 0.3s ease;
  }
  
  &:hover {
    opacity: 1;
    
    &::after {
      width: 100%;
    }
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    &:hover::after {
      width: 100%;
    }
  }
`;

const Divider = styled.span`
  margin: 0 16px;
  color: ${theme.colors.primary.text};
  opacity: 0.3;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin: 0 8px;
  }
`;

const PointsList = styled(motion.div)`
  margin-bottom: 40px;
`;

const Point = styled(motion.div)`
  position: relative;
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 1.25rem;
  font-weight: 300;
  line-height: 1.4;
  color: ${theme.colors.primary.text};
  margin-bottom: 24px;
  text-align: center;
  padding-top: 16px;
  
  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    transform: translateX(-50%);
    width: var(--line-width, 40px);
    height: 1px;
    background-color: ${theme.colors.accent.main};
  }
  
  /* Hide line for first point */
  &:first-child::before {
    display: none;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 1.1rem;
    
    &::before {
      width: 40px;
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1rem;
    margin-bottom: 20px;
    
    &::before {
      width: 32px;
    }
  }
`;

const CallToAction = styled(motion.p)`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.95rem;
  font-style: italic;
  color: ${theme.colors.primary.text};
  text-align: center;
  margin-bottom: 60px;
  
  .accent {
    color: ${theme.colors.accent.deep};
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 0.9rem;
  }
`;


const OurMovements = () => {
  const [activeMovement, setActiveMovement] = useState('reformer');
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  
  const navigate = useNavigate();
  
  const [positionImages, setPositionImages] = useState([null, null, null]); // [left, top-right, bottom-right]
  const [nextImageIndex, setNextImageIndex] = useState(0);
  
  const handleImageClick = () => {
    const currentMovement = movements[activeMovement];
    navigate(`/movement/${currentMovement.slug}`);
  };
  
  const handleLearnMore = () => {
    const currentMovement = movements[activeMovement];
    navigate(`/movement/${currentMovement.slug}`);
  };
  
  const currentMovement = movements[activeMovement];
  
  // Initialize with first 3 images when movement changes
  useEffect(() => {
    const images = currentMovement.images;
    const now = Date.now();
    const initialImages = [
      { src: images[0], key: `img-0-${now}`, timestamp: now },
      { src: images[1] || images[0], key: `img-1-${now}`, timestamp: now + 1 },
      { src: images[2] || images[0], key: `img-2-${now}`, timestamp: now + 2 }
    ];
    setPositionImages(initialImages);
    setNextImageIndex(3 % images.length);
  }, [activeMovement, currentMovement.images]);
  
  // Rotate images when there are more than 3
  useEffect(() => {
    const images = currentMovement.images;
    if (images.length <= 3) return;
    
    const interval = setInterval(() => {
      setPositionImages(prev => {
        // Find the position with the oldest image (smallest timestamp)
        let oldestIndex = 0;
        let oldestTimestamp = prev[0]?.timestamp || 0;
        
        for (let i = 1; i < prev.length; i++) {
          if (prev[i]?.timestamp < oldestTimestamp) {
            oldestTimestamp = prev[i].timestamp;
            oldestIndex = i;
          }
        }
        
        // Replace the oldest image with the next image
        const newImages = [...prev];
        newImages[oldestIndex] = {
          src: images[nextImageIndex],
          key: `img-${nextImageIndex}-${Date.now()}`,
          timestamp: Date.now()
        };
        return newImages;
      });
      
      setNextImageIndex(prev => (prev + 1) % images.length);
    }, theme.animations.imageRotationInterval);
    
    return () => clearInterval(interval);
  }, [activeMovement, currentMovement.images, nextImageIndex]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0, // No stagger - text and images appear simultaneously
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  const descriptionVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 }
  };

  return (
    <SectionWrapper
      ref={ref}
      variants={containerVariants}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
    >
      <Grid>
        <ImagesColumn>
          {positionImages.map((imageData, index) => (
            <ImageContainer
              key={`position-${index}`}
              variants={imageVariants}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={commonVariants.buttonHover}
              transition={{
                duration: 0.8,
                ease: 'easeOut',
              }}
              onClick={handleImageClick}
            >
              <AnimatePresence mode="wait">
                {imageData && (
                  <MediaWrapper
                    key={imageData.key}
                    as={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <Media 
                      src={imageData.src}
                      alt={`${currentMovement.name} ${index + 1}`}
                    />
                  </MediaWrapper>
                )}
              </AnimatePresence>
            </ImageContainer>
          ))}
        </ImagesColumn>
        
        <ContentColumn>
          <Heading variants={itemVariants}>
            Our <em>Movements</em>
          </Heading>
          
          <CallToAction variants={itemVariants}>
            Join the Movement. The <span className="accent">Sol</span> Movement.
          </CallToAction>
          
          <Selector variants={itemVariants}>
            <MovementOptions>
              {Object.entries(movements).map(([key, movement], index, array) => (
                <React.Fragment key={key}>
                  <MovementOption
                    $isActive={activeMovement === key}
                    onClick={() => setActiveMovement(key)}
                  >
                    {movement.name}
                  </MovementOption>
                  {index < array.length - 1 && <Divider>/</Divider>}
                </React.Fragment>
              ))}
            </MovementOptions>
          </Selector>
          
          <AnimatePresence mode="wait">
            <PointsList
              key={activeMovement}
              variants={descriptionVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3 }}
            >
              {currentMovement.points.map((point, index) => (
                <Point
                  key={point}
                  initial={{ 
                    opacity: 0, 
                    y: 10,
                    "--line-width": "0px"
                  }}
                  animate={{ 
                    opacity: 1, 
                    y: 0,
                    "--line-width": "40px"
                  }}
                  transition={{ 
                    delay: index * 0.15, 
                    duration: 0.4,
                    ease: "easeOut"
                  }}
                  style={{
                    "--line-width": "0px"
                  }}
                >
                  {point}
                </Point>
              ))}
            </PointsList>
          </AnimatePresence>
          
          <motion.div 
            variants={itemVariants}
            style={{ textAlign: 'center', marginTop: '10px' }}
          >
            <Button onClick={handleLearnMore}>
              Learn More
            </Button>
          </motion.div>
        </ContentColumn>
      </Grid>
    </SectionWrapper>
  );
};

export default OurMovements;